import { Icon } from '@fluentui/react';
import { Image, type NavigationElementConfig, type NavigationSectionConfig } from '@h2oai/ui-kit';
import type { ReactNode } from 'react';

import type { User } from '../../ai.h2o.cloud.appstore';
import type { Permissions } from '../../authz/providers/RoleProvider';
import { AppStoreMenuName } from '../../discovery-service/constants';
import { FeatureStoreNavigation } from '../../featurestore/FeatureStoreNavigation';
import { ROUTES } from '../../pages/MLOps/constants';
import { type RoutePath, RoutePaths } from '../../pages/RoutePaths';
import { ALL_ROUTE_DEFINITIONS, isHomePageAvailable, type RouteDefinition } from '../../pages/Routes/routeDefinitions';
import type { CloudPlatformDiscoveryConfig, EnvironmentAndMenu } from '../../utils/contexts';
import { NavigationIcon } from './components/NavigationIcon';
import { ReactComponent as AdminCenterIcon } from './icons/AdminCenter.svg';
import { ReactComponent as AIEnginesIcon } from './icons/AIEngines-C.svg';
import { ReactComponent as AIUnitConsumptionIcon } from './icons/AIUnitConsumption.svg';
import { ReactComponent as AIUnitHourlyIcon } from './icons/AIUnitHourly.svg';
import { ReactComponent as AppAliasesIcon } from './icons/AppAliases.svg';
import { ReactComponent as ApplicationIcon } from './icons/Application.svg';
import { ReactComponent as AuthIcon } from './icons/Auth.svg';
import { ReactComponent as EnterpriseIcon } from './icons/Enterprise.svg';
import { ReactComponent as EnvirnmentInfoIcon } from './icons/EnvirnmentInfo.svg';
import { ReactComponent as FWManagementIcon } from './icons/FirewallManagement.svg';
import { ReactComponent as HomeIcon } from './icons/Home.svg';
import { ReactComponent as InstancesIcon } from './icons/Instance.svg';
import { ReactComponent as AppstoreIcon } from './icons/Marketplace.svg';
import { ReactComponent as MLOpsIcon } from './icons/MLOps.svg';
import { ReactComponent as SecretsIcon } from './icons/Secrets.svg';
import { ReactComponent as SettingsIcon } from './icons/Settings.svg';
import { ReactComponent as MemberManagementIcon } from './icons/UserManagement.svg';

/** Extended NavigationElementConfig with optional routePath for access checking */
type NavElementConfig = NavigationElementConfig & { routePath?: RoutePath };

const routeDefinitionMap = new Map<RoutePath, RouteDefinition>(ALL_ROUTE_DEFINITIONS.map((r) => [r.path, r]));

export type NavContext = {
  env: EnvironmentAndMenu;
  user: User;
  isAuthenticated: boolean;
  cloudPlatformDiscovery?: CloudPlatformDiscoveryConfig;
  permissions?: Permissions;
  activeWorkspaceName?: string;
  hasAuthzPermission: (permission: { action: string; resource?: string }) => boolean;
  isLoadingPermissions?: boolean;
  mlopsMonitoringUrl?: string;
};

/**
 * Check if a route is accessible based on its route definition
 */
export const isRouteAccessible = (routePath: RoutePath, ctx: NavContext): boolean => {
  const route = routeDefinitionMap.get(routePath);

  if (!route) return true; // Route not in definitions (external link, dynamic route, etc.)

  const { env, user, isAuthenticated, cloudPlatformDiscovery, permissions, hasAuthzPermission, isLoadingPermissions } =
    ctx;

  if (route.requiresAuth && !isAuthenticated) return false;
  if (route.requiresAdmin && !(user.isAdmin || user.isSuperAdmin)) return false;
  if (route.requiresEnvCondition && !route.requiresEnvCondition(env)) return false;
  if (route.requiresDiscoveryKey && !cloudPlatformDiscovery?.[route.requiresDiscoveryKey]) return false;

  // Check callback-based permission (used by Orchestrator)
  if (route.requiresPermission && permissions) {
    return route.requiresPermission(permissions);
  }

  // Skip authz permission checks while loading - show items optimistically
  if (isLoadingPermissions) return true;

  // Check OR: user must have at least ONE
  if (route.requiresAuthzPermissionOR?.length && !route.requiresAuthzPermissionOR.some((p) => hasAuthzPermission(p))) {
    return false;
  }

  // Check AND: user must have ALL
  if (
    route.requiresAuthzPermissionAND?.length &&
    !route.requiresAuthzPermissionAND.every((p) => hasAuthzPermission(p))
  ) {
    return false;
  }

  return true;
};

export type NavItemDef = {
  key: string;
  name: string;
  to: RoutePath | string;
  dataTest: string;
  image: ReactNode;
  exact?: boolean;
  section?: NavigationSectionConfig | ((ctx: NavContext) => NavigationSectionConfig);
  /** Additional visibility check beyond route definition */
  when?: (ctx: NavContext) => boolean;
};

export type NavSectionDef = {
  key: string;
  name?: string;
  items: NavItemDef[];
  /** Section-level visibility check */
  when?: (ctx: NavContext) => boolean;
};

/**
 * Filter nav items based on route accessibility and custom conditions
 */
export const filterNavItems = (items: NavItemDef[], ctx: NavContext): NavigationElementConfig[] => {
  return items
    .filter((item) => {
      // Check route accessibility for internal routes
      if (Object.values(RoutePaths).includes(item.to as RoutePath)) {
        if (!isRouteAccessible(item.to as RoutePath, ctx)) return false;
      }
      // Check custom visibility condition
      if (item.when && !item.when(ctx)) return false;
      return true;
    })
    .map((item) => {
      const section = typeof item.section === 'function' ? item.section(ctx) : item.section;
      return {
        key: item.key,
        name: item.name,
        to: item.to,
        dataTest: item.dataTest,
        image: item.image,
        exact: item.exact,
        ...(section ? { section } : {}),
      } as NavigationElementConfig;
    });
};

/**
 * Filter nav sections and their items
 */
export const filterNavSections = (
  sections: NavSectionDef[],
  ctx: NavContext
): Array<{ key: string; name?: string; elements: NavigationElementConfig[] }> => {
  return sections
    .filter((section) => !section.when || section.when(ctx))
    .map((section) => ({
      key: section.key,
      name: section.name,
      elements: filterNavItems(section.items, ctx),
    }))
    .filter((section) => section.elements.length > 0);
};

// ============================================================================
// NAVIGATION SECTIONS DEFINITIONS
// ============================================================================

export const MAIN_NAV_ITEMS: NavItemDef[] = [
  {
    key: AppStoreMenuName.home,
    name: AppStoreMenuName.home,
    to: RoutePaths.HOME,
    dataTest: 'home--main-menu-item',
    image: <NavigationIcon src={HomeIcon} />,
    when: isHomePageAvailable,
  },
  {
    key: AppStoreMenuName.appStore,
    name: AppStoreMenuName.appStore,
    to: RoutePaths.APPSTORE,
    dataTest: 'app-store--main-menu-item',
    image: <NavigationIcon src={AppstoreIcon} />,
  },
];

export const MY_NAV_ITEMS: NavItemDef[] = [
  {
    key: AppStoreMenuName.myAiEngines,
    name: AppStoreMenuName.myAiEngines,
    to: RoutePaths.MY_AI_ENGINES,
    dataTest: 'my-ai-engines--main-menu-item',
    image: <NavigationIcon src={AIEnginesIcon} />,
  },
  {
    key: AppStoreMenuName.myApps,
    name: AppStoreMenuName.myApps,
    to: RoutePaths.MYAPPS,
    dataTest: 'my-apps--main-menu-item',
    image: <NavigationIcon src={ApplicationIcon} />,
  },
  {
    key: AppStoreMenuName.myInstances,
    name: AppStoreMenuName.myInstances,
    to: RoutePaths.INSTANCES,
    dataTest: 'my-instances--main-menu-item',
    image: <NavigationIcon src={InstancesIcon} />,
  },
  {
    key: AppStoreMenuName.secureStoreSecrets,
    name: AppStoreMenuName.secureStoreSecrets,
    to: RoutePaths.SECURE_STORE_SECRETS,
    dataTest: 'secure-store-secrets--main-menu-item',
    image: <NavigationIcon src={SecretsIcon} />,
  },
  {
    key: 'audit-trail',
    name: 'Audit Trail',
    to: RoutePaths.AUDIT_TRAIL,
    dataTest: 'audit-trail--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="ScheduleEventAction" />,
  },
  {
    key: 'manage-workspaces',
    name: 'Workspaces',
    to: RoutePaths.MANAGE_WORKSPACES,
    dataTest: 'manage-workspaces--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="Stack" />,
  },
  {
    key: AppStoreMenuName.drive,
    name: AppStoreMenuName.drive,
    to: RoutePaths.DRIVE,
    dataTest: 'drive--main-menu-item',
    image: <Icon style={{ fontSize: 18 }} iconName="HardDriveGroup" />,
  },
];

export const OPERATIONS_NAV_ITEMS: NavItemDef[] = [
  {
    key: 'ml-ops',
    name: 'MLOps',
    to: RoutePaths.MLOPS_ENTRY,
    dataTest: 'mlops--main-menu-item',
    image: <NavigationIcon src={MLOpsIcon} />,
    section: (ctx) => ({
      top: [
        {
          key: 'mlops-sub',
          elements: getMlopsSubNavItems(ctx),
        },
      ],
    }),
  },
  {
    key: 'orchestrator',
    name: 'Orchestrator',
    to: RoutePaths.ORCHESTRATOR_ENTRY,
    dataTest: 'orchestrator--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="Flow" />,
    section: (ctx) => ({
      top: [
        {
          key: 'orchestrator-sub',
          elements: getOrchestratorSubNavItems(ctx),
        },
      ],
    }),
  },
  {
    key: 'featurestore',
    name: 'Feature Store',
    to: RoutePaths.FEATURESTORE_ROOT,
    dataTest: 'featurestore--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="TableComputed" />,
    section: { renderContent: () => <FeatureStoreNavigation /> },
  },
];

export const ADMIN_NAV_ITEMS: NavItemDef[] = [
  {
    key: AppStoreMenuName.adminAiEngines,
    name: AppStoreMenuName.adminAiEngines,
    to: RoutePaths.ADMIN_AI_ENGINES,
    dataTest: 'admin-ai-engines--main-menu-item',
    exact: true,
    image: <NavigationIcon src={AIEnginesIcon} />,
  },
  {
    key: AppStoreMenuName.aiEngineSettings,
    name: AppStoreMenuName.aiEngineSettings,
    to: RoutePaths.AI_ENGINE_SETTINGS,
    dataTest: 'ai-engine-settings--main-menu-item',
    image: <NavigationIcon src={AIEnginesIcon} />,
  },
  {
    key: AppStoreMenuName.adminMarketplaceApps,
    name: AppStoreMenuName.adminMarketplaceApps,
    to: RoutePaths.ADMIN_MARKETPLACE_APPS,
    dataTest: 'admin-marketplace-apps--main-menu-item',
    image: <Image src="/logo.svg" alt="" style={{ width: 20, height: 20 }} />,
  },
  {
    key: AppStoreMenuName.adminApps,
    name: AppStoreMenuName.adminApps,
    to: RoutePaths.ADMIN_APPS,
    dataTest: 'admin-apps--main-menu-item',
    image: <NavigationIcon src={ApplicationIcon} />,
  },
  {
    key: AppStoreMenuName.adminInstances,
    name: AppStoreMenuName.adminInstances,
    to: RoutePaths.ADMIN_INSTANCES,
    dataTest: 'admin-instances--main-menu-item',
    image: <NavigationIcon src={InstancesIcon} />,
  },
  {
    key: 'admin-apps-secrets',
    name: 'App Secrets',
    to: RoutePaths.ADMIN_SECRETS,
    dataTest: 'admin-apps-secrets--main-menu-item',
    image: <NavigationIcon src={SecretsIcon} />,
  },
  {
    key: 'admin-instances-aliases',
    name: 'App Aliases',
    to: RoutePaths.ADMIN_ALIASES,
    dataTest: 'admin-instances-aliases--main-menu-item',
    image: <NavigationIcon src={AppAliasesIcon} />,
  },
  {
    key: AppStoreMenuName.kernelSettings,
    name: AppStoreMenuName.kernelSettings,
    to: RoutePaths.NOTEBOOK_SETTINGS,
    dataTest: 'notebook-admin-settings--main-menu-item',
    image: <NavigationIcon src={SettingsIcon} />,
  },
  {
    key: AppStoreMenuName.secureStoreOAuthClients,
    name: AppStoreMenuName.secureStoreOAuthClients,
    to: RoutePaths.SECURE_STORE_OAUTH_CLIENTS,
    dataTest: 'secure-store-admin-oauth--main-menu-item',
    image: <NavigationIcon src={AuthIcon} />,
  },
  {
    key: 'admin-manage-workspaces',
    name: 'Workspaces',
    to: RoutePaths.ADMIN_MANAGE_WORKSPACES,
    dataTest: 'admin-manage-workspaces--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="Stack" />,
  },

  {
    key: 'mlops-admin-deployments',
    name: 'MLOps Deployments',
    to: RoutePaths.MLOPS_ADMIN_DEPLOYMENTS,
    dataTest: 'mlops-admin-deployments--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="Deploy" />,
  },
  {
    key: 'mlops-admin-models',
    name: 'MLOps Models',
    to: RoutePaths.MLOPS_ADMIN_MODELS,
    dataTest: 'mlops-admin-models--main-menu-item',
    image: <Icon style={{ fontSize: 20 }} iconName="HomeGroup" />,
  },

  {
    key: AppStoreMenuName.adminCenter,
    name: AppStoreMenuName.adminCenter,
    to: RoutePaths.ADMIN_CENTER_ENV_INFO, // Default, will be overridden in `to` logic
    dataTest: 'mcac--main-menu-item',
    image: <NavigationIcon src={AdminCenterIcon} />,
    section: (ctx) => getAdminCenterSection(ctx),
  },
];

// ============================================================================
// HELPER FUNCTIONS FOR DYNAMIC SECTIONS
// ============================================================================

const getMlopsSubNavItems = (ctx: NavContext): NavElementConfig[] => {
  const { activeWorkspaceName, mlopsMonitoringUrl } = ctx;
  const items: NavElementConfig[] = [
    {
      key: 'mlops-home',
      name: 'Home',
      to: `${RoutePaths.MLOPS_ENTRY}/${activeWorkspaceName}`,
      routePath: RoutePaths.MLOPS,
      exact: true,
      dataTest: 'mlops-home--sub-main-menu-item',
      image: <Icon style={{ fontSize: 18 }} iconName="Home" />,
    },
    {
      key: 'mlops-models',
      name: 'Models',
      to: `/mlops/${activeWorkspaceName}/models`,
      exact: false,
      routePath: RoutePaths.MLOPS_MODELS,
      image: <Icon style={{ fontSize: 18 }} iconName="HomeGroup" />,
      dataTest: 'mlops-models--sub-main-menu-item',
    },
    {
      key: 'mlops-deployments',
      name: 'Real-time deployments',
      to: `/mlops/${activeWorkspaceName}/deployments`,
      routePath: RoutePaths.MLOPS_DEPLOYMENTS,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="PublishContent" />,
      dataTest: 'mlops-deployments--sub-main-menu-item',
    },
    {
      key: 'mlops-jobs',
      name: 'Batch scoring jobs',
      to: `/mlops/${activeWorkspaceName}${ROUTES.BATCH_SCORING}`,
      routePath: RoutePaths.MLOPS_BATCH_SCORING,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="AssessmentGroup" />,
      dataTest: 'mlops-jobs--sub-main-menu-item',
    },
    {
      dataTest: 'mlops-deployments--sub-main-menu-item',
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="BIDashboard" />,
      isExternal: true,
      key: 'mlops-monitoring-external',
      name: 'Monitoring UI',
      to: mlopsMonitoringUrl,
      more: {
        text: <Icon iconName="NavigateExternalInline" />,
      },
    },
  ];

  return items.filter((item) => !item.routePath || isRouteAccessible(item.routePath, ctx));
};

const getOrchestratorSubNavItems = (ctx: NavContext): NavElementConfig[] => {
  const { activeWorkspaceName } = ctx;

  const items: NavElementConfig[] = [
    {
      key: 'orchestrator-home',
      name: 'Home',
      to: activeWorkspaceName
        ? `${RoutePaths.ORCHESTRATOR_ENTRY}/${activeWorkspaceName}`
        : RoutePaths.MANAGE_WORKSPACES,
      routePath: RoutePaths.ORCHESTRATOR,
      exact: true,
      image: <Icon style={{ fontSize: 18 }} iconName="Home" />,
      dataTest: 'orchestrator-home--sub-main-menu-item',
    },
    {
      key: 'orchestrator-executor-pools',
      name: 'Executor pools',
      to: `/orchestrator/${activeWorkspaceName}/executorPools`,
      routePath: RoutePaths.ORCHESTRATOR_EXECUTOR_POOLS,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="Devices2" />,
      dataTest: 'orchestrator-executor-pools--sub-main-menu-item',
    },
    {
      key: 'orchestrator-runnables',
      name: 'Runnables',
      to: `/orchestrator/${activeWorkspaceName}/runnables`,
      routePath: RoutePaths.ORCHESTRATOR_RUNNABLES,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="Rocket" />,
      dataTest: 'orchestrator-runnables--sub-main-menu-item',
    },
    {
      key: 'orchestrator-workflows',
      name: 'Workflows',
      to: `/orchestrator/${activeWorkspaceName}/workflows`,
      routePath: RoutePaths.ORCHESTRATOR_WORKFLOWS,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="Flow" />,
      dataTest: 'orchestrator-workflows--sub-main-menu-item',
    },
    {
      key: 'orchestrator-executions',
      name: 'Active executions',
      to: `/orchestrator/${activeWorkspaceName}/activeExecutions`,
      routePath: RoutePaths.ORCHESTRATOR_ACTIVE_EXECUTIONS,
      exact: false,
      image: <Icon style={{ fontSize: 18 }} iconName="Play" />,
      dataTest: 'orchestrator-executions--sub-main-menu-item',
    },
  ];

  return items.filter((item) => !item.routePath || isRouteAccessible(item.routePath, ctx));
};

const getAdminCenterSection = (ctx: NavContext): NavigationSectionConfig => {
  const isAdmin = ctx.env?.menu?.user?.isAdmin;

  if (isAdmin) {
    return {
      top: [
        {
          key: 'mcac-environment',
          name: 'Environment Management',
          elements: [
            {
              key: 'env-info',
              name: 'Environment Info',
              to: RoutePaths.ADMIN_CENTER_ENV_INFO,
              image: <NavigationIcon src={EnvirnmentInfoIcon} />,
              dataTest: 'mcac-env-info--sub-main-menu-item',
            },
            {
              key: 'fw-management',
              name: 'Firewall Management',
              to: RoutePaths.ADMIN_CENTER_FW_MANAGEMENT,
              image: <NavigationIcon src={FWManagementIcon} />,
              dataTest: 'mcac-fw-management--sub-main-menu-item',
            },
            {
              key: 'member-management',
              name: 'Member Management',
              to: RoutePaths.ADMIN_CENTER_MEMBER_MANAGEMENT,
              image: <NavigationIcon src={MemberManagementIcon} />,
              dataTest: 'mcac-member-management--sub-main-menu-item',
            },
            {
              key: 'idp-management',
              name: 'IdP Management',
              to: RoutePaths.ADMIN_CENTER_IDP_MANAGEMENT,
              image: <Icon style={{ fontSize: 18 }} iconName="Contact" />,
              dataTest: 'mcac-idp-management--sub-main-menu-item',
            },
            {
              key: 'custom-group-management',
              name: 'Custom Groups',
              to: RoutePaths.ADMIN_CENTER_CUSTOM_GROUPS,
              image: <Icon style={{ fontSize: 18 }} iconName="Group" />,
              dataTest: 'mcac-custom-group-management--sub-main-menu-item',
            },
            {
              key: 'trusted-role-management',
              name: 'Trusted Roles',
              to: RoutePaths.ADMIN_CENTER_TRUSTED_ROLES,
              image: <Icon style={{ fontSize: 18 }} iconName="SecurityGroup" />,
              dataTest: 'mcac-trusted-role-management--sub-main-menu-item',
            },
          ],
        },
        {
          key: 'mcac-ai-usage',
          name: 'AI Unit Reporting',
          elements: [
            {
              key: 'ai-unit-consumption',
              name: 'AI Unit Consumption',
              to: RoutePaths.ADMIN_CENTER_AI_UNIT_CONSUMPTION,
              image: <NavigationIcon src={AIUnitConsumptionIcon} />,
              dataTest: 'mcac-aiunit-max--sub-main-menu-item',
            },
            {
              key: 'ai-unit-hourly',
              name: 'AI Unit Hourly',
              to: RoutePaths.ADMIN_CENTER_AI_UNIT_HOURLY,
              image: <NavigationIcon src={AIUnitHourlyIcon} />,
              dataTest: 'mcac-aiunit-hourly--sub-main-menu-item',
            },
            {
              key: 'ai-unit-workspace',
              name: 'Workspace',
              to: RoutePaths.ADMIN_CENTER_AI_UNIT_WORKSPACE,
              image: <Icon style={{ fontSize: 20 }} iconName="Stack" />,
              dataTest: 'mcac-aiunit-workspace--sub-main-menu-item',
            },
          ],
        },
        {
          key: 'mcac-telemetry',
          name: 'Analytics',
          elements: [
            {
              key: 'aiem',
              name: 'AI Engines',
              to: RoutePaths.ADMIN_CENTER_AIEM,
              image: <NavigationIcon src={AIEnginesIcon} />,
              dataTest: 'mcac-aiem-events--sub-main-menu-item',
            },
            {
              key: 'mlops-events',
              name: 'MLOps',
              to: RoutePaths.ADMIN_CENTER_MLOPS,
              image: <NavigationIcon src={MLOpsIcon} />,
              dataTest: 'mcac-mlops-events--sub-main-menu-item',
            },
            {
              key: 'app-event',
              name: 'Apps',
              to: RoutePaths.ADMIN_CENTER_APP_EVENT,
              image: <NavigationIcon src={ApplicationIcon} />,
              dataTest: 'mcac-app-events--sub-main-menu-item',
            },
            {
              key: 'instance-event',
              name: 'App Instances',
              to: RoutePaths.ADMIN_CENTER_INSTANCE_EVENT,
              image: <NavigationIcon src={InstancesIcon} />,
              dataTest: 'mcac-instance-events--sub-main-menu-item',
            },
            {
              key: 'user-login',
              name: 'Logins',
              to: RoutePaths.ADMIN_CENTER_USER_LOGIN,
              image: <Icon style={{ fontSize: 18 }} iconName="UserGauge" />,
              dataTest: 'mcac-user-logins--sub-main-menu-item',
            },
            {
              key: 'h2o-gpte',
              name: 'h2oGPTe',
              to: RoutePaths.ADMIN_CENTER_H2O_GPTE,
              image: <NavigationIcon src={EnterpriseIcon} />,
              dataTest: 'mcac-h2o-gpte--sub-main-menu-item',
            },
            {
              key: 'escorer',
              name: 'eScorer',
              to: RoutePaths.ADMIN_CENTER_ESCORER,
              image: <Icon style={{ fontSize: 18 }} iconName="ReceiptForward" />,
              dataTest: 'mcac-escorer--sub-main-menu-item',
            },
          ],
        },
      ],
    };
  }

  // Non-admin user section
  return {
    top: [
      {
        key: 'mcac-ai-usage',
        name: 'AI Unit Reporting',
        elements: [
          {
            key: 'ai-unit-consumption',
            name: 'AI Unit Consumption',
            to: RoutePaths.ADMIN_CENTER_AI_UNIT_CONSUMPTION,
            image: <Icon style={{ fontSize: 18 }} iconName="Chart" />,
            dataTest: 'mcac-aiunit-max--sub-main-menu-item',
          },
        ],
      },
    ],
  };
};
